/**
 * Offline / failure queue for crmActivitySessions writes (POC path).
 * Retries on a timer; drops entries older than 7 days.
 */

import type { CrmActivitySession } from '../types';

const QUEUE_KEY = 'lctf_crm_activity_session_write_queue_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_QUEUE = 200;

type QueuedSession = Omit<CrmActivitySession, 'id'> & { queuedAt: number };

type SessionWriter = (session: Omit<CrmActivitySession, 'id'>) => Promise<string>;

let writer: SessionWriter | null = null;

/** Register the Firestore writer (avoids a circular import with the session service). */
export function registerCrmActivitySessionWriter(fn: SessionWriter): void {
  writer = fn;
}

function readQueue(): QueuedSession[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueuedSession[];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - MAX_AGE_MS;
    return parsed
      .filter((row) => row && typeof row.queuedAt === 'number' && row.queuedAt >= cutoff)
      .slice(-MAX_QUEUE)
      .map((row) => ({
        ...row,
        startedAt: new Date(row.startedAt),
        endedAt: new Date(row.endedAt),
        createdAt: new Date(row.createdAt),
      }));
  } catch {
    return [];
  }
}

function writeQueue(rows: QueuedSession[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const serializable = rows.slice(-MAX_QUEUE).map((row) => ({
      ...row,
      startedAt: row.startedAt instanceof Date ? row.startedAt.toISOString() : row.startedAt,
      endedAt: row.endedAt instanceof Date ? row.endedAt.toISOString() : row.endedAt,
      createdAt: row.createdAt instanceof Date ? row.createdAt.toISOString() : row.createdAt,
    }));
    localStorage.setItem(QUEUE_KEY, JSON.stringify(serializable));
  } catch (err) {
    console.error('[CrmActivityQueue] Failed to persist queue:', err);
  }
}

export function enqueueCrmActivitySessionWrite(session: Omit<CrmActivitySession, 'id'>): void {
  const queue = readQueue();
  queue.push({ ...session, queuedAt: Date.now() });
  writeQueue(queue);
}

let flushInFlight: Promise<void> | null = null;

export async function flushCrmActivitySessionWriteQueue(): Promise<void> {
  if (flushInFlight) return flushInFlight;
  flushInFlight = (async () => {
    if (!writer) return;
    const queue = readQueue();
    if (queue.length === 0) return;
    const remaining: QueuedSession[] = [];
    for (const row of queue) {
      const { queuedAt: _queuedAt, ...session } = row;
      try {
        await writer(session);
      } catch (err) {
        console.error('[CrmActivityQueue] Retry failed:', err);
        remaining.push(row);
      }
    }
    writeQueue(remaining);
  })().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

export function startCrmActivitySessionWriteQueuePump(intervalMs = 30_000): () => void {
  void flushCrmActivitySessionWriteQueue();
  const timer = setInterval(() => {
    void flushCrmActivitySessionWriteQueue();
  }, intervalMs);
  const onOnline = () => {
    void flushCrmActivitySessionWriteQueue();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
  }
  return () => {
    clearInterval(timer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
    }
  };
}
